/**
 * KILNCOR CUSTOM — this file does not exist in upstream Horizon.
 * See KILNCOR-CUSTOMIZATIONS.md before deleting or renaming it.
 *
 * Back-in-stock notification button.
 *
 * Takes the place of Horizon's disabled "Sold out" button when the selected
 * variant is genuinely out of stock. It cannot live *inside* that button:
 * product-form.js calls AddToCartComponent.disable() on every variant change,
 * which force-sets `disabled = true` and would make the bell unclickable. So
 * this element is a sibling and hides the Sold out button while it is active.
 *
 * Because product-form.js only morphs [ref="addToCartButton"], a sibling gets
 * no automatic update on variant change. This element therefore listens for
 * productSelect itself, exactly as product-price.js and product-inventory.js do.
 */

import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Submission adapters, one per back-in-stock provider.
 *
 * Each returns a promise that resolves on success and rejects on failure.
 * Add a provider here and reference it by name from the CONFIG block in
 * snippets/kilncor-notify-button.liquid.
 *
 * @type {Record<string, (args: {email: string, variantId: string, productId: string, element: HTMLElement}) => Promise<void>>}
 */
const ADAPTERS = {
  /**
   * Klaviyo client-side back-in-stock subscription.
   *
   * Request shape verified against Klaviyo's reference on 2026-07-30:
   * https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
   *
   * Klaviyo versions this API by date. If subscriptions start failing after a
   * Klaviyo change, `revision` below is the first thing to check.
   *
   * The company_id is Klaviyo's PUBLIC API key — safe to ship in theme source.
   * A successful call returns 202 Accepted, not 200.
   */
  async klaviyo({ email, variantId, element }) {
    const companyId = element.dataset.klaviyoKey;
    if (!companyId) throw new Error('[kilncor-notify] Missing Klaviyo public key');

    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2026-07-15',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                // Klaviyo's catalog ID format for Shopify-synced variants. The
                // variant must already exist in Klaviyo's catalog, so the
                // Shopify integration's initial sync has to have finished.
                data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` },
              },
            },
          },
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`[kilncor-notify] Klaviyo responded ${response.status}`);
    }
  },

  /**
   * Your own Shopify App Proxy endpoint. Shopify signs and forwards the request
   * to your backend, so no CORS setup and no exposed credentials.
   */
  async proxy({ email, variantId, productId, element }) {
    const path = element.dataset.proxyPath;
    if (!path) throw new Error('[kilncor-notify] Missing proxy path');

    const response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, variant_id: variantId, product_id: productId }),
    });

    if (!response.ok) {
      throw new Error(`[kilncor-notify] Proxy responded ${response.status}`);
    }
  },
};

/**
 * @typedef {Object} KilncorNotifyRefs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} panel
 * @property {HTMLInputElement} email
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} message
 *
 * @extends {Component<KilncorNotifyRefs>}
 */
class KilncorNotify extends Component {
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();

    this.refs.trigger?.addEventListener('click', this.#togglePanel);
    this.refs.submit?.addEventListener('click', this.#handleSubmit);
    this.refs.email?.addEventListener('keydown', this.#handleKeydown);

    this.#syncSoldOutButton();

    this.closest('.shopify-section, dialog')?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.trigger?.removeEventListener('click', this.#togglePanel);
    this.refs.submit?.removeEventListener('click', this.#handleSubmit);
    this.refs.email?.removeEventListener('keydown', this.#handleKeydown);

    this.closest('.shopify-section, dialog')?.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );
  }

  get #isActive() {
    return this.dataset.active === 'true';
  }

  /**
   * Hides Horizon's disabled Sold out button while the bell is showing, and
   * restores it otherwise. Scoped to the nearest button group so a quick-add
   * modal never reaches into the main product form.
   */
  #syncSoldOutButton() {
    this.toggleAttribute('hidden', !this.#isActive);

    const group = this.closest('.product-form-buttons');
    const addToCart = group?.querySelector('add-to-cart-component');
    if (addToCart instanceof HTMLElement) {
      addToCart.toggleAttribute('hidden', this.#isActive);
    }
  }

  #togglePanel = () => {
    const { trigger, panel, email } = this.refs;
    if (!trigger || !panel) return;

    const open = trigger.getAttribute('aria-expanded') !== 'true';
    trigger.setAttribute('aria-expanded', String(open));
    panel.toggleAttribute('hidden', !open);

    if (open) email?.focus();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    // The panel sits inside Horizon's product <form>; Enter would otherwise
    // submit an add-to-cart for a variant that is out of stock.
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.#handleSubmit();
  };

  /** @param {string} text @param {'success'|'error'} tone */
  #setMessage(text, tone) {
    const { message } = this.refs;
    if (!message) return;
    message.textContent = text;
    message.classList.toggle('kilncor-notify__message--error', tone === 'error');
    message.classList.toggle('kilncor-notify__message--success', tone === 'success');
  }

  #handleSubmit = async () => {
    if (this.#submitting) return;

    const { email, submit, panel } = this.refs;
    const value = email?.value.trim() ?? '';

    if (!value || !email?.checkValidity()) {
      this.#setMessage(this.dataset.messageInvalid || 'Please enter a valid email address.', 'error');
      email?.focus();
      return;
    }

    const adapter = ADAPTERS[this.dataset.provider ?? ''];
    if (!adapter) {
      console.warn(`[kilncor-notify] Unknown provider "${this.dataset.provider}"`);
      this.#setMessage(this.dataset.messageError || 'Something went wrong.', 'error');
      return;
    }

    this.#submitting = true;
    if (submit) submit.disabled = true;
    this.#setMessage('', 'success');

    try {
      await adapter({
        email: value,
        variantId: this.dataset.variantId ?? '',
        productId: this.dataset.productId ?? '',
        element: this,
      });

      this.#setMessage(this.dataset.messageSuccess || 'You are on the list.', 'success');
      if (email) email.value = '';
      // Leave the panel open so the confirmation is visible, but the form is
      // done — collapse the input row.
      panel?.classList.add('kilncor-notify__panel--done');
    } catch (error) {
      console.warn('[kilncor-notify] Subscription failed:', error);
      this.#setMessage(this.dataset.messageError || 'Something went wrong.', 'error');
    } finally {
      this.#submitting = false;
      if (submit) submit.disabled = false;
    }
  };

  /**
   * Re-render from freshly rendered Liquid when the variant changes, so
   * data-active and data-variant-id track the newly selected variant.
   * @param {CustomEvent & {promise: Promise<{detail: any}>}} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const incoming = html.querySelector('kilncor-notify');

        // No element in the new markup means the variant is purchasable again.
        this.dataset.active = incoming?.dataset.active ?? 'false';
        if (incoming?.dataset.variantId) this.dataset.variantId = incoming.dataset.variantId;

        this.#reset();
        this.#syncSoldOutButton();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[kilncor-notify] Event promise rejected:', error);
      });
  };

  /** Collapse the panel and clear state — the previous variant's signup is done. */
  #reset() {
    const { trigger, panel, email } = this.refs;
    trigger?.setAttribute('aria-expanded', 'false');
    panel?.setAttribute('hidden', '');
    panel?.classList.remove('kilncor-notify__panel--done');
    if (email) email.value = '';
    this.#setMessage('', 'success');
  }
}

if (!customElements.get('kilncor-notify')) {
  customElements.define('kilncor-notify', KilncorNotify);
}
